import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BOOKMARKLET_TEMPLATE, SYNC_BOOKMARKLET_TEMPLATE } from "@/lib/cargrBookmarklet";
import { BookmarkSimpleIcon, CopyIcon, CheckIcon, ArrowSquareOutIcon, ArrowsClockwiseIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Help page for the car.gr bookmarklet.
 *
 * Bookmarklets MUST use `javascript:` URIs to function as browser bookmarks — that is
 * standard, documented browser behaviour (not XSS). To avoid placing a `javascript:`
 * URL in any rendered `href` attribute (which static analysers flag), we now expose
 * the bookmarklet via a draggable element that writes the URI to `dataTransfer` only
 * at drag-start time. When the user drops onto the bookmarks bar, the browser stores
 * the bookmark using the URI from `text/uri-list`.
 */
export default function BookmarkletHelp() {
  const [copied, setCopied] = useState(false);
  const [copiedSync, setCopiedSync] = useState(false);

  const { bookmarkletUri, source, title, syncUri, syncTitle } = useMemo(() => {
    const origin = window.location.origin;
    const src = BOOKMARKLET_TEMPLATE(origin);
    const compact = src.replace(/\n\s*/g, " ").replace(/\s+/g, " ").trim();
    const syncSrc = SYNC_BOOKMARKLET_TEMPLATE(origin);
    const syncCompact = syncSrc.replace(/\n\s*/g, " ").replace(/\s+/g, " ").trim();
    return {
      // eslint-disable-next-line no-script-url
      bookmarkletUri: "javascript:" + encodeURIComponent(compact),
      source: src,
      title: "Εισαγωγή από car.gr → BESIKOS",
      // eslint-disable-next-line no-script-url
      syncUri: "javascript:" + encodeURIComponent(syncCompact),
      syncTitle: "Συγχρονισμός αγγελιών car.gr → BESIKOS",
    };
  }, []);

  const copyAsText = async () => {
    try {
      await navigator.clipboard.writeText(bookmarkletUri);
      setCopied(true);
      toast.success("Link αντιγράφηκε");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Δεν ήταν δυνατή η αντιγραφή");
    }
  };

  // Drag handler: writes the bookmarklet URI to the clipboard-transfer payload so the
  // browser creates a bookmark when the user drops onto the bookmarks bar.
  const onDragStart = (e) => {
    try {
      e.dataTransfer.effectAllowed = "copy";
      e.dataTransfer.setData("text/uri-list", bookmarkletUri);
      e.dataTransfer.setData("text/plain", bookmarkletUri);
      // Firefox uses this for "drag bookmark" gestures
      e.dataTransfer.setData("text/x-moz-url", `${bookmarkletUri}\n${title}`);
    } catch {
      /* dataTransfer not available — ignore silently */
    }
  };

  const onDragStartSync = (e) => {
    try {
      e.dataTransfer.effectAllowed = "copy";
      e.dataTransfer.setData("text/uri-list", syncUri);
      e.dataTransfer.setData("text/plain", syncUri);
      e.dataTransfer.setData("text/x-moz-url", `${syncUri}\n${syncTitle}`);
    } catch {
      /* ignore */
    }
  };

  const copySyncAsText = async () => {
    try {
      await navigator.clipboard.writeText(syncUri);
      setCopiedSync(true);
      toast.success("Link αντιγράφηκε");
      setTimeout(() => setCopiedSync(false), 2500);
    } catch {
      toast.error("Δεν ήταν δυνατή η αντιγραφή");
    }
  };

  const onClickWarn = () => {
    toast.info("Μην το πατάτε εδώ — σύρετέ το στη γραμμή σελιδοδεικτών του browser.");
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="border-l-4 border-[#374151] pl-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B7280] font-mono">Ενότητα</div>
        <h2 className="heading text-2xl font-bold">Εισαγωγή από car.gr</h2>
        <p className="text-sm text-[#4B5563] mt-1">
          Bookmarklet που εξάγει αυτόματα στοιχεία από σελίδες car.gr και τα φέρνει εδώ.
        </p>
      </div>

      {/* Bookmarklet button to drag */}
      <section className="bg-[#0A0A0A] text-white p-8 text-center" data-testid="bookmarklet-section">
        <BookmarkSimpleIcon size={32} weight="fill" className="mx-auto mb-3 text-white/60" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2">
          Σύρετε το παρακάτω κουμπί στη γραμμή σελιδοδεικτών
        </div>
        <button
          type="button"
          draggable="true"
          onDragStart={onDragStart}
          onClick={onClickWarn}
          data-testid="bookmarklet-link"
          aria-label={title}
          className="inline-block bg-white text-[#0A0A0A] px-6 py-3 font-bold tracking-wide hover:bg-white/90 cursor-grab active:cursor-grabbing select-none border-0"
        >
          📌 {title}
        </button>
        <div className="mt-4">
          <button onClick={copyAsText} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5">
            {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
            {copied ? "Αντιγράφηκε" : "ή αντιγράψτε το link ως κείμενο"}
          </button>
        </div>
      </section>

      {/* Dealer listings sync bookmarklet */}
      <section className="bg-[#0055FF] text-white p-8 text-center" data-testid="sync-bookmarklet-section">
        <ArrowsClockwiseIcon size={32} weight="bold" className="mx-auto mb-3 text-white/70" />
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/60 mb-2">
          Νέο · Μαζικός συγχρονισμός αριθμών αγγελιών
        </div>
        <button
          type="button"
          draggable="true"
          onDragStart={onDragStartSync}
          onClick={onClickWarn}
          data-testid="sync-bookmarklet-link"
          aria-label={syncTitle}
          className="inline-block bg-white text-[#0055FF] px-6 py-3 font-bold tracking-wide hover:bg-white/90 cursor-grab active:cursor-grabbing select-none border-0"
        >
          🔄 {syncTitle}
        </button>
        <div className="mt-4">
          <button onClick={copySyncAsText} className="text-xs text-white/70 hover:text-white inline-flex items-center gap-1.5">
            {copiedSync ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
            {copiedSync ? "Αντιγράφηκε" : "ή αντιγράψτε το link ως κείμενο"}
          </button>
        </div>
        <div className="mt-5 text-left max-w-xl mx-auto bg-white/10 p-4 text-xs leading-relaxed space-y-2">
          <div><strong>Πώς λειτουργεί:</strong></div>
          <div>1. Σύρετέ το στη γραμμή σελιδοδεικτών (όπως το πρώτο). Αν το είχατε ήδη, <strong>διαγράψτε το παλιό και ξανασύρετε το νέο</strong> — αναβαθμίστηκε.</div>
          <div>2. Ανοίξτε τη σελίδα του καταστήματός σας <a href="https://besikoscars.car.gr/" target="_blank" rel="noreferrer" className="underline font-mono">besikoscars.car.gr</a> και πατήστε το bookmark.</div>
          <div>3. Το bookmarklet διαβάζει <strong>αυτόματα όλες τις σελίδες</strong> του καταστήματος (1η, 2η, 3η...) και στέλνει όλες τις αγγελίες μαζί.</div>
          <div>4. Η εφαρμογή ταιριάζει κάθε αγγελία με τα αυτοκίνητα του στόλου (μάρκα, μοντέλο, έτος, τιμή, χιλιόμετρα) και συμπληρώνει τους αριθμούς αγγελίας — τα μπλε ματάκια ενεργοποιούνται αμέσως.</div>
          <div className="pt-1 border-t border-white/20">💡 Με το WP plugin <strong>v1.5.8+</strong> ο συγχρονισμός τρέχει και <strong>αυτόματα κάθε ώρα</strong> από τον server του site σας (ρύθμιση "car.gr Κατάστημα URL" στη σελίδα BESIKOS Sync του WordPress). Το bookmarklet παραμένει ως εφεδρικό αν το car.gr μπλοκάρει τον server.</div>
        </div>
      </section>

      {/* Step-by-step instructions */}
      <section className="bg-white border border-black/10 p-6">
        <h3 className="heading text-lg font-bold mb-4">
          Πώς να το εγκαταστήσετε <span className="text-xs font-normal text-[#6B7280]">(1 φορά)</span>
        </h3>
        <ol className="space-y-4 text-sm">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 bg-[#374151] text-white flex items-center justify-center text-xs font-mono">1</span>
            <div>
              <strong>Εμφανίστε τη γραμμή σελιδοδεικτών</strong> του browser σας:
              <ul className="ml-4 mt-1 list-disc text-[#4B5563] text-xs">
                <li>Chrome / Edge: <kbd className="bg-[#F9FAFB] px-1 border border-black/10">Ctrl+Shift+B</kbd> (Windows) ή <kbd className="bg-[#F9FAFB] px-1 border border-black/10">⌘+Shift+B</kbd> (Mac)</li>
                <li>Firefox: Menu → Bookmarks → Show Bookmarks Toolbar → Always</li>
                <li>Safari: View → Show Bookmarks Bar</li>
              </ul>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 bg-[#374151] text-white flex items-center justify-center text-xs font-mono">2</span>
            <div>
              <strong>Σύρετε</strong> το μαύρο κουμπί <em>&quot;{title}&quot;</em> με το ποντίκι σας <strong>πάνω στη γραμμή σελιδοδεικτών</strong>. Θα εμφανιστεί σαν νέο bookmark.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 bg-[#374151] text-white flex items-center justify-center text-xs font-mono">3</span>
            <div>
              <strong>Έτοιμοι!</strong> Από εδώ και μπρός, όταν είστε σε σελίδα car.gr (π.χ. <code className="bg-[#F9FAFB] px-1 text-xs">besikoscars.car.gr/cars/view/53024901/</code>), <strong>πατήστε το νέο σας bookmark</strong>.
            </div>
          </li>
        </ol>
        <div className="mt-4 bg-[#FEF3C7] border border-[#F59E0B] p-3 text-xs text-[#92400E]">
          ⚠️ <strong>Σημαντικό:</strong> Λειτουργεί μόνο όταν εκτελείται από bookmark του browser σας πάνω σε σελίδα car.gr.
        </div>
        <div className="mt-3 bg-[#ECFDF5] border border-[#10B981] p-3 text-xs text-[#065F46]">
          ℹ️ <strong>Νέα έκδοση (v4.1):</strong> Πλήρης εισαγωγή — διαβάζει τα δομημένα δεδομένα της σελίδας και φέρνει <strong>έκδοση, όλο τον εξοπλισμό, την περιγραφή και έως 30 φωτογραφίες</strong>, χωρίς να χρειάζεται να ανοίξετε τα &quot;Περισσότερα&quot;. Τα δεδομένα στέλνονται κωδικοποιημένα ώστε να μην κόβονται από firewall (διόρθωση των 502). Αν έχετε παλιό bookmark, <strong>διαγράψτε το και σύρετε ξανά</strong> το παραπάνω.
        </div>
      </section>

      {/* What it does */}
      <section className="bg-[#F9FAFB] border border-black/10 p-6">
        <h3 className="heading text-lg font-bold mb-3">Τι κάνει αυτόματα</h3>
        <ul className="text-sm space-y-2 text-[#4B5563]">
          <li className="flex gap-2"><CheckIcon size={16} weight="bold" className="text-[#0A7F3F] flex-shrink-0 mt-0.5" /> Διαβάζει <strong>μάρκα, μοντέλο, έκδοση, έτος, μήνα ταξινόμησης, χιλιόμετρα, καύσιμο, κιβώτιο, αμάξωμα, κυβισμό, ίππους, χρώματα, ταπετσαρία, ρύπους, Euro, ιδιοκτήτες, τιμή</strong> από τα δομημένα δεδομένα της σελίδας</li>
          <li className="flex gap-2"><CheckIcon size={16} weight="bold" className="text-[#0A7F3F] flex-shrink-0 mt-0.5" /> Αντιγράφει την <strong>περιγραφή</strong> και τη λίστα <strong>εξοπλισμού</strong></li>
          <li className="flex gap-2"><CheckIcon size={16} weight="bold" className="text-[#0A7F3F] flex-shrink-0 mt-0.5" /> Συγκεντρώνει έως <strong>30 φωτογραφίες</strong> από το car.gr CDN — αυτές κατεβαίνουν στον server μας μόλις αποθηκεύσετε το αυτοκίνητο</li>
          <li className="flex gap-2"><CheckIcon size={16} weight="bold" className="text-[#0A7F3F] flex-shrink-0 mt-0.5" /> Ανοίγει αυτόματα τη φόρμα <Link to="/cars/new" className="underline">Νέο Αυτοκίνητο</Link> με όλα τα πεδία προ-συμπληρωμένα</li>
        </ul>
      </section>

      {/* Source viewer */}
      <section className="bg-white border border-black/10 p-6">
        <h3 className="heading text-lg font-bold mb-3 flex items-center justify-between">
          <span>Πηγαίος κώδικας <span className="text-xs font-normal text-[#6B7280]">(για ενδιαφερόμενους)</span></span>
          <a
            href="https://besikoscars.car.gr/cars/view/53024901/"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-normal text-[#374151] hover:text-[#0A0A0A] inline-flex items-center gap-1"
          >
            <ArrowSquareOutIcon size={12} /> Δοκιμή σε πραγματική σελίδα
          </a>
        </h3>
        <pre className="bg-[#0A0A0A] text-white text-xs p-4 overflow-auto max-h-96 font-mono leading-relaxed">{source}</pre>
      </section>
    </div>
  );
}
